import "moment/locale/it";

import * as React from "react";

import { Admin, Loading, Resource } from "react-admin";
import {
  WorkflowProvider,
  createI18nProvider,
  useAuthProvider,
  useDataProvider,
  useI18nCatcher,
  useI18nLanguages,
} from "ra-friendsofbabba";

import { API_URL } from "./config";
import Layout from "./component/Layout";
import LoginPage from "./component/page/LoginPage";
import ProfilePage from "./component/page/ProfilePage";
import { Route } from "react-router-dom";
import docFiles from "./doc-files";
import executiveProjectEquipments from "./executive-project-equipments";
import executiveProjectWpActivities from "./executive-project-wp-activities";
import executiveProjectWpDeliverables from "./executive-project-wp-deliverables";
import executiveProjectWps from "./executive-project-wps";
import executiveProjects from "./executive-projects";
import finalBalances from "./final-balances";
import itMessages from "ra-language-italian";
import languageMessages from "./language-messages";
import mirExplorer from "./mir-explorer";
import mirImportLogs from "./mir-import-logs";
import mirImports from "./mir-imports";
import notifications from "./notifications";
import researchProjectActivities from "./research-project-activities";
import researchProjectDocs from "./research-project-docs";
import researchProjectEquipments from "./research-project-equipments";
import researchProjectEvals from "./research-project-evals";
import researchProjectPatents from "./research-project-patents";
import researchProjectStaffUsers from "./research-project-staff-users";
import researchProjects from "./research-projects";
import substitutiveDeclarations from "./substitutive-declarations";
import technicalReportReplies from "./technical-report-replies";
import technicalReports from "./technical-reports";
import theme from "./theme";
import userBreaks from "./user-breaks";
import userOutputs from "./user-outputs";
import userOutputsMedia from "./user-outputs-media";
import users from "./users";

const App = () => {
  const apiUrl = API_URL;
  const dataProvider = useDataProvider({
    apiUrl,
    fileFields: [
      "media",
      "doc_media",
      "id_doc_media",
      "signed_doc_media",
      "deliverables_media",
    ],
  });
  const authProvider = useAuthProvider({ apiUrl });
  const languages = useI18nLanguages({ apiUrl });
  useI18nCatcher({ apiUrl });

  if (languages?.loading) {
    return (
      <Loading loadingPrimary="Loading" loadingSecondary="Please wait..." />
    );
  }

  return (
    <WorkflowProvider apiUrl={apiUrl}>
      <Admin
        title="riparti.dev"
        theme={theme}
        dataProvider={dataProvider}
        authProvider={authProvider}
        i18nProvider={createI18nProvider({
          languages: {
            ...languages?.data,
            it: {
              ...languages?.data?.it,
              ...itMessages,
            },
          },
          locale: "it",
        })}
        loginPage={LoginPage}
        layout={Layout}
        customRoutes={[
          <Route key="profile" path="/profile" component={ProfilePage} />,
        ]}
      >
        <Resource name="notifications" {...notifications} />
        <Resource name="users" {...users} />
        <Resource name="user-profile-logs" />
        <Resource name="language-messages" {...languageMessages} />
        <Resource name="roles" />
        <Resource name="resources" />
        <Resource name="groups" />
        <Resource name="languages" />
        <Resource name="profile" />

        <Resource name="production-chains" />
        <Resource name="research-areas" />
        <Resource name="research-project-doc-types" />
        <Resource name="equipment-types" />
        <Resource name="company-types" />
        <Resource name="company-type-entries" />
        <Resource name="staff-user-roles" />
        <Resource name="patent-statuses" />
        <Resource name="patent-types" />
        <Resource name="municipalities" />
        <Resource name="municipalities/provinces" />
        <Resource name="research-projects" {...researchProjects} />
        <Resource
          name="research-project-activities"
          {...researchProjectActivities}
        />
        <Resource name="research-project-docs" {...researchProjectDocs} />
        <Resource
          name="research-project-equipments"
          {...researchProjectEquipments}
        />
        <Resource name="research-project-evals" {...researchProjectEvals} />
        <Resource name="research-project-patents" {...researchProjectPatents} />
        <Resource
          name="research-project-staff-users"
          {...researchProjectStaffUsers}
        />
        <Resource name="mir-explorer/privati" {...mirExplorer} />
        <Resource name="mir-imports" {...mirImports} />
        <Resource name="mir-import-logs" {...mirImportLogs} />
        <Resource name="user-outputs" {...userOutputs} />
        <Resource name="executive-projects" {...executiveProjects} />
        <Resource name="executive-project-wps" {...executiveProjectWps} />
        <Resource
          name="executive-project-wp-activities"
          {...executiveProjectWpActivities}
        />
        <Resource name="venue-types" />
        <Resource
          name="executive-project-wp-deliverables"
          {...executiveProjectWpDeliverables}
        />
        <Resource
          name="executive-project-equipments"
          {...executiveProjectEquipments}
        />
        <Resource name="technical-reports" {...technicalReports} />
        <Resource name="technical-report-replies" {...technicalReportReplies} />
        <Resource name="educational-qualifications" />
        <Resource name="occupations" />
        <Resource name="occupation-durations" />
        <Resource name="user-outputs-media" {...userOutputsMedia} />
        <Resource name="user-output-media-types" />
        <Resource name="user-outputs/mir-codes" />
        <Resource name="user-breaks" {...userBreaks} />
        <Resource name="user-break-reasons" />
        <Resource name="substitutive-declarations-users" />
        <Resource
          name="substitutive-declarations"
          {...substitutiveDeclarations}
        />
        <Resource name="user-logs" />
        <Resource name="doc-files" {...docFiles} />
        <Resource name="doc-file-types" />
        <Resource name="final-balances" {...finalBalances} />

        <Resource name="workflow/transactions/research-projects" />
        <Resource name="workflow/transactions/research-project-evals" />
        <Resource name="workflow/transactions/user-outputs" />
        <Resource name="workflow/transactions/executive-projects" />
        <Resource name="workflow/transactions/technical-reports" />
        <Resource name="workflow/transactions/substitutive-declarations" />
      </Admin>
    </WorkflowProvider>
  );
};
export default App;
